import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import CompanySelector from "../components/CompanySelector";
import MessagingPanel from "../components/MessagingPanel";
import NotificationPanel from "../components/NotificationPanel";
import { useUnreadMessages } from "../hooks/useUnreadMessages";
import { useNotifications } from "../hooks/useNotifications";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Phone, User, MapPin, Briefcase, Calendar as CalendarIcon,
  Star, ThumbsUp, ThumbsDown, AlertCircle, Send, Clock,
  Ban, ChevronRight, LogOut, Loader2, CheckCircle2, Keyboard,
  Save, Zap, Mail, Euro, TrendingUp, MessageCircle, History,
  ChevronLeft, Edit3, X, PhoneOff, BarChart3, Target, PhoneMissed, Bell
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeleprospectorDashboard = () => {
  const { user, logout } = useAuth();
  const { selectedCompany, companyId } = useCompany();
  const { unreadCount, refreshUnreadCount } = useUnreadMessages();
  const { 
    notifications, 
    unreadCount: notifUnreadCount, 
    connected: wsConnected,
    markAsRead: markNotifAsRead,
    markAllAsRead: markAllNotifsAsRead
  } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [nextCustomer, setNextCustomer] = useState(null); // Pre-fetched next customer
  const [callback, setCallback] = useState(null);
  const [priority, setPriority] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMessaging, setShowMessaging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [salesReps, setSalesReps] = useState([]);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // 'saving', 'saved', null
  const [showShortcuts, setShowShortcuts] = useState(false);
  
  // Call form state
  const [jobWentWell, setJobWentWell] = useState(null);
  const [satisfactionScore, setSatisfactionScore] = useState(null);
  const [hasIssues, setHasIssues] = useState(null);
  const [issueType, setIssueType] = useState("");
  const [issueNotes, setIssueNotes] = useState("");
  const [npsScore, setNpsScore] = useState(null);
  const [notes, setNotes] = useState("");
  
  // Modals
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [appointmentTime, setAppointmentTime] = useState("");
  const [appointmentPurpose, setAppointmentPurpose] = useState("");
  const [appointmentDuration, setAppointmentDuration] = useState(60);
  const [selectedSalesRep, setSelectedSalesRep] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [personalAppointments, setPersonalAppointments] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [callbackDate, setCallbackDate] = useState(null);
  const [callbackTime, setCallbackTime] = useState("");
  
  // Sales stats state
  const [salesStats, setSalesStats] = useState(null);
  
  // My call stats state
  const [myCallStats, setMyCallStats] = useState(null);
  const [showMyStats, setShowMyStats] = useState(false);

  // Today's calls history state
  const [todaysCalls, setTodaysCalls] = useState([]);
  const [showTodaysHistory, setShowTodaysHistory] = useState(false);
  const [editingPreviousCall, setEditingPreviousCall] = useState(false);

  // Pending callbacks state
  const [pendingCallbacks, setPendingCallbacks] = useState([]);
  const [showCallbacksPanel, setShowCallbacksPanel] = useState(false);

  // Refs for autosave
  const autoSaveTimerRef = useRef(null);
  const formDataRef = useRef({});

  // Fetch sales stats
  const fetchSalesStats = useCallback(async () => {
    try {
      const params = companyId ? { company_id: companyId, period: "month" } : { period: "month" };
      const response = await axios.get(`${API}/stats/sales`, { params });
      setSalesStats(response.data);
    } catch (error) {
      console.error("Error fetching sales stats:", error);
    }
  }, [companyId]);

  // Fetch today's calls for history
  const fetchTodaysCalls = useCallback(async () => {
    if (!companyId) return;
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const response = await axios.get(`${API}/calls`, { 
        params: { 
          company_id: companyId,
          date_from: today,
          date_to: today,
          my_calls: true
        } 
      });
      setTodaysCalls(response.data || []);
    } catch (error) {
      console.error("Error fetching today's calls:", error);
    }
  }, [companyId]);

  // Fetch my call stats
  const fetchMyCallStats = useCallback(async () => {
    try {
      const params = companyId ? { company_id: companyId } : {};
      const response = await axios.get(`${API}/stats/my-calls`, { params });
      setMyCallStats(response.data);
    } catch (error) {
      console.error("Error fetching my call stats:", error);
    }
  }, [companyId]);

  // Fetch pending callbacks
  const fetchPendingCallbacks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/callbacks/pending`);
      setPendingCallbacks(response.data || []);
    } catch (error) {
      console.error("Error fetching pending callbacks:", error);
    }
  }, []);

  // Cancel a callback
  const cancelCallback = async (callbackId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler ce rappel ?")) return;
    try {
      await axios.delete(`${API}/callbacks/${callbackId}`);
      toast.success("Rappel annulé");
      fetchPendingCallbacks();
    } catch (error) {
      toast.error("Erreur lors de l'annulation");
    }
  };

  // Load customer from callback
  const loadCustomerFromCallback = async (callback) => {
    try {
      const response = await axios.get(`${API}/customers/${callback.customer_id}`);
      setCustomer(response.data);
      setCallback({
        scheduled_date: callback.scheduled_date,
        scheduled_time: callback.scheduled_time
      });
      setPriority("callback");
      setShowCallbacksPanel(false);
      toast.info(`Fiche chargée - Rappel prévu à ${callback.scheduled_time}`);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
  };

  // Prefetch next customer for instant loading
  const prefetchNextCall = useCallback(async () => {
    try {
      if (!companyId) return;
      const response = await axios.get(`${API}/calls/next`, { params: { company_id: companyId } });
      if (response.data.customer && response.data.customer.id !== customer?.id) {
        setNextCustomer(response.data);
      }
    } catch (error) {
      console.error("Prefetch error:", error);
    }
  }, [customer?.id, companyId]);

  const fetchSalesReps = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users/sales-reps`, { params: { company_id: companyId } });
      setSalesReps(response.data);
    } catch (error) {
      console.error("Error fetching sales reps:", error);
    }
  }, [companyId]);

  // Fetch available slots when sales rep or date changes
  const fetchAvailableSlots = useCallback(async () => {
    if (!selectedSalesRep || !appointmentDate) {
      setAvailableSlots([]);
      return;
    }
    
    setLoadingSlots(true);
    try {
      const dateStr = format(appointmentDate, "yyyy-MM-dd");
      const response = await axios.get(`${API}/appointments/slots`, {
        params: {
          sales_rep_id: selectedSalesRep,
          date: dateStr,
          duration_minutes: appointmentDuration
        }
      });
      setAvailableSlots(response.data.available_slots);
      setPersonalAppointments(response.data.personal_appointments || []);
      // Auto-select first available slot
      if (response.data.available_slots.length > 0 && !appointmentTime) {
        setAppointmentTime(response.data.available_slots[0]);
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      setAvailableSlots([]);
      setPersonalAppointments([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [selectedSalesRep, appointmentDate, appointmentDuration, appointmentTime]);

  useEffect(() => {
    if (showAppointmentModal) {
      fetchAvailableSlots();
    }
  }, [showAppointmentModal, selectedSalesRep, appointmentDate, appointmentDuration, fetchAvailableSlots]);

  // Fetch sales stats when company changes
  useEffect(() => {
    if (companyId) {
      fetchSalesStats();
      fetchTodaysCalls();
      fetchMyCallStats();
      fetchPendingCallbacks();
    }
  }, [companyId, fetchSalesStats, fetchTodaysCalls, fetchMyCallStats, fetchPendingCallbacks]);

  // Reset customer when company changes
  useEffect(() => {
    setCustomer(null);
    setNextCustomer(null);
    resetForm();
  }, [companyId]);

  const fetchNextCall = useCallback(async (usePreFetched = false) => {
    if (!companyId) {
      toast.error("Veuillez sélectionner une société");
      return;
    }
    
    // If we have prefetched data, use it instantly
    if (usePreFetched && nextCustomer) {
      setCustomer(nextCustomer.customer);
      setCallback(nextCustomer.callback);
      setPriority(nextCustomer.priority);
      setNextCustomer(null);
      resetForm();
      // Prefetch the next one in background
      setTimeout(() => prefetchNextCall(), 500);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/calls/next`, { params: { company_id: companyId } });
      setCustomer(response.data.customer);
      setCallback(response.data.callback);
      setPriority(response.data.priority);
      resetForm();
      // Prefetch the next one in background
      setTimeout(() => prefetchNextCall(), 1000);
    } catch (error) {
      console.error("Error fetching next call:", error);
      toast.error("Erreur lors du chargement du prochain appel");
    } finally {
      setLoading(false);
    }
  }, [nextCustomer, prefetchNextCall, companyId]);

  useEffect(() => {
    if (companyId) {
      fetchSalesReps();
    }
  }, [fetchSalesReps, companyId]);

  // Auto-save effect
  useEffect(() => {
    if (!customer) return;

    // Update form data ref
    formDataRef.current = {
      customer_id: customer.id,
      job_went_well: jobWentWell,
      satisfaction_score: satisfactionScore,
      has_issues: hasIssues,
      issue_type: issueType,
      issue_notes: issueNotes,
      nps_score: npsScore,
      notes: notes
    };

    // Debounced autosave
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Only autosave if we have some data
    if (jobWentWell !== null || satisfactionScore !== null || hasIssues !== null || npsScore !== null || notes) {
      setAutoSaveStatus('saving');
      autoSaveTimerRef.current = setTimeout(() => {
        // Save to localStorage as draft
        localStorage.setItem(`call_draft_${customer.id}`, JSON.stringify(formDataRef.current));
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(null), 2000);
      }, 1000);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [customer, jobWentWell, satisfactionScore, hasIssues, issueType, issueNotes, npsScore, notes]);

  // Load draft on customer change
  useEffect(() => {
    if (customer) {
      const draft = localStorage.getItem(`call_draft_${customer.id}`);
      if (draft) {
        try {
          const data = JSON.parse(draft);
          if (data.job_went_well !== null) setJobWentWell(data.job_went_well);
          if (data.satisfaction_score !== null) setSatisfactionScore(data.satisfaction_score);
          if (data.has_issues !== null) setHasIssues(data.has_issues);
          if (data.issue_type) setIssueType(data.issue_type);
          if (data.issue_notes) setIssueNotes(data.issue_notes);
          if (data.nps_score !== null) setNpsScore(data.nps_score);
          if (data.notes) setNotes(data.notes);
          toast.info("Brouillon restauré", { duration: 2000 });
        } catch (e) {
          console.error("Error loading draft:", e);
        }
      }
    }
  }, [customer?.id]);

  // Refs for keyboard shortcuts to avoid stale closures
  const customerRef = useRef(customer);
  const showAppointmentModalRef = useRef(showAppointmentModal);
  const showCallbackModalRef = useRef(showCallbackModal);
  const submittingRef = useRef(submitting);

  useEffect(() => {
    customerRef.current = customer;
    showAppointmentModalRef.current = showAppointmentModal;
    showCallbackModalRef.current = showCallbackModal;
    submittingRef.current = submitting;
  }, [customer, showAppointmentModal, showCallbackModal, submitting]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const currentCustomer = customerRef.current;
      const isAppointmentOpen = showAppointmentModalRef.current;
      const isCallbackOpen = showCallbackModalRef.current;
      const isSubmitting = submittingRef.current;
      
      // Don't trigger if in a modal
      if (isAppointmentOpen || isCallbackOpen) {
        if (e.key === 'Escape') {
          setShowAppointmentModal(false);
          setShowCallbackModal(false);
        }
        return;
      }

      // Don't trigger if typing in an input/textarea (except for special combos)
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Ctrl+Enter to complete call from any field
        if (e.key === 'Enter' && e.ctrlKey && currentCustomer && !isSubmitting) {
          e.preventDefault();
          document.getElementById('complete-call-btn')?.click();
        }
        return;
      }

      // No customer loaded - only Enter to start
      if (!currentCustomer) {
        if (e.key === 'Enter') {
          e.preventDefault();
          document.querySelector('[data-testid="next-call-btn"]')?.click();
        }
        return;
      }

      // Global shortcuts (when customer is loaded and not in input)
      switch (e.key) {
        case 'Enter':
          if (!isSubmitting) {
            e.preventDefault();
            document.getElementById('complete-call-btn')?.click();
          }
          break;
        case 'Escape':
          e.preventDefault();
          document.querySelector('[data-testid="skip-next-btn"]')?.click();
          break;
        case '?':
          e.preventDefault();
          setShowShortcuts(true);
          break;
        default:
          break;
      }
      
      // Letter shortcuts (case insensitive)
      const key = e.key.toLowerCase();
      
      // O/N for job went well (Oui/Non)
      if (key === 'o' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        setJobWentWell(true);
      }
      if (key === 'n' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        setJobWentWell(false);
      }

      // P/S for issues (Problème/Sans problème)
      if (key === 'p' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        setHasIssues(true);
      }
      if (key === 's' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        setHasIssues(false);
      }

      // R for RDV modal
      if (key === 'r' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        setShowAppointmentModal(true);
      }

      // C for Callback modal
      if (key === 'c' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        setShowCallbackModal(true);
      }

      // D for Do Not Contact
      if (key === 'd' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        document.querySelector('[data-testid="do-not-contact-btn"]')?.click();
      }
      
      // Number keys for satisfaction (1-5)
      if (['1', '2', '3', '4', '5'].includes(e.key) && !e.ctrlKey && !e.altKey && !e.metaKey) {
        setSatisfactionScore(parseInt(e.key));
      }
      
      // Alt + Number for NPS (0-9, Alt+0 for 10)
      if (e.altKey && /^[0-9]$/.test(e.key)) {
        e.preventDefault();
        setNpsScore(e.key === '0' ? 10 : parseInt(e.key));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const resetForm = () => {
    setJobWentWell(null);
    setSatisfactionScore(null);
    setHasIssues(null);
    setIssueType("");
    setIssueNotes("");
    setNpsScore(null);
    setNotes("");
    setCallbackDate(null);
    setCallbackTime("");
  };

  const canSendReview = () => {
    // Allow sending review if satisfaction is good (>=4) OR NPS is good (>=8)
    // hasIssues should be false OR null/undefined
    const goodSatisfaction = satisfactionScore >= 4;
    const goodNPS = npsScore >= 8;
    const noIssues = hasIssues !== true;
    
    return (goodSatisfaction || goodNPS) && noIssues;
  };

  const getReviewBlockReason = () => {
    if (hasIssues === true) return "Le client a signalé des problèmes";
    if (satisfactionScore < 4 && npsScore < 8) return "Notes trop basses (satisfaction < 4 et NPS < 8)";
    if (!customer?.email) return "Le client n'a pas d'email";
    return null;
  };

  const [googleReviewLink, setGoogleReviewLink] = useState("");

  // Get Google Review link from selected company
  useEffect(() => {
    if (selectedCompany?.google_review_link) {
      setGoogleReviewLink(selectedCompany.google_review_link);
    } else {
      setGoogleReviewLink("");
    }
  }, [selectedCompany]);

  const handleSendReview = async () => {
    if (!customer?.email) {
      toast.error("Le client n'a pas d'adresse email");
      return;
    }

    if (!googleReviewLink) {
      toast.error("Lien Google Review non configuré pour cette société (voir Admin > Sociétés)");
      return;
    }
    
    // Create mailto link with pre-filled content
    const subject = encodeURIComponent("Votre avis nous intéresse !");
    const body = encodeURIComponent(
`Bonjour ${customer.name},

Nous espérons que vous êtes satisfait(e) des travaux réalisés.

Votre avis compte beaucoup pour nous ! Pourriez-vous prendre quelques secondes pour nous laisser un avis sur Google ?

${googleReviewLink}

Merci beaucoup !
L'équipe Vanessa`
    );
    
    // Open default email client
    window.open(`mailto:${customer.email}?subject=${subject}&body=${body}`, "_blank");
    
    // Log the review request
    try {
      await axios.post(`${API}/reviews/log?customer_id=${customer.id}`);
    } catch (error) {
      // Silent fail - just logging
    }
    
    toast.success("Boîte mail ouverte !");
  };

  const handleBookAppointment = async () => {
    if (!appointmentDate || !appointmentTime || !selectedSalesRep || !appointmentPurpose) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    if (!companyId) {
      toast.error("Veuillez sélectionner une société");
      return;
    }

    if (!customer || !customer.id) {
      toast.error("Erreur: client non sélectionné");
      return;
    }

    try {
      const appointmentData = {
        customer_id: customer.id,
        sales_rep_id: selectedSalesRep,
        date: format(appointmentDate, "yyyy-MM-dd"),
        time: appointmentTime,
        duration_minutes: appointmentDuration,
        purpose: appointmentPurpose,
        teleprospector_notes: notes || ""
      };
      
      await axios.post(`${API}/appointments?company_id=${companyId}`, appointmentData);
      
      toast.success("Rendez-vous créé ! Le commercial sera notifié.");
      setShowAppointmentModal(false);
      setAppointmentDate(null);
      setAppointmentTime("");
      setAppointmentPurpose("");
      setAppointmentDuration(60);
      setSelectedSalesRep("");
      setAvailableSlots([]);
    } catch (error) {
      console.error("Error creating appointment:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création du rendez-vous");
    }
  };

  const handleScheduleCallback = () => {
    if (!callbackDate || !callbackTime) {
      toast.error("Veuillez sélectionner une date et une heure");
      return;
    }
    setShowCallbackModal(false);
    toast.success("Rappel programmé !");
  };

  const handleDoNotContact = async () => {
    try {
      await axios.put(`${API}/customers/${customer.id}/do-not-contact`);
      toast.success("Client marqué comme ne pas contacter");
      // Clear draft
      localStorage.removeItem(`call_draft_${customer.id}`);
      fetchNextCall(true);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleCompleteCall = async () => {
    if (submitting) return;
    if (!companyId) {
      toast.error("Aucune société sélectionnée");
      return;
    }
    if (!customer?.id) {
      toast.error("Aucun client sélectionné");
      return;
    }
    setSubmitting(true);
    
    try {
      let callbackScheduled = null;
      if (callbackDate && callbackTime) {
        const dt = new Date(callbackDate);
        const [hours, minutes] = callbackTime.split(":");
        dt.setHours(parseInt(hours), parseInt(minutes));
        callbackScheduled = dt.toISOString();
      }

      await axios.post(`${API}/calls`, {
        customer_id: customer.id,
        job_went_well: jobWentWell,
        satisfaction_score: satisfactionScore,
        has_issues: hasIssues,
        issue_type: issueType || null,
        issue_notes: issueNotes || null,
        nps_score: npsScore,
        callback_scheduled: callbackScheduled,
        notes: notes || null,
        call_status: "completed"
      }, { params: { company_id: companyId } });

      // Clear draft
      localStorage.removeItem(`call_draft_${customer.id}`);
      
      toast.success("Appel enregistré !", { duration: 1500 });
      
      // Refresh today's calls history and stats
      fetchTodaysCalls();
      fetchMyCallStats();
      
      // Instant transition to next call
      fetchNextCall(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle unreachable customer - auto schedule callback in 2 hours
  const handleUnreachable = async () => {
    if (submitting) return;
    if (!companyId || !customer?.id) return;
    
    setSubmitting(true);
    try {
      // Schedule callback in 2 hours
      const callbackTime = new Date();
      callbackTime.setHours(callbackTime.getHours() + 2);
      
      await axios.post(`${API}/calls`, {
        customer_id: customer.id,
        notes: "Client injoignable - rappel automatique programmé",
        call_status: "unreachable",
        callback_scheduled: callbackTime.toISOString()
      }, { params: { company_id: companyId } });

      localStorage.removeItem(`call_draft_${customer.id}`);
      toast.info("Client injoignable - Rappel dans 2h", { duration: 2000 });
      
      fetchTodaysCalls();
      fetchMyCallStats();
      fetchNextCall(true);
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityBadge = () => {
    const badges = {
      callback: { label: "Rappel", className: "bg-amber-100 text-amber-700 border-amber-200" },
      post_job: { label: "Post-travaux", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
      one_year_follow_up: { label: "Suivi 1 an", className: "bg-blue-100 text-blue-700 border-blue-200" },
      new_customer: { label: "Nouveau", className: "bg-slate-100 text-slate-700 border-slate-200" },
      yearly_rotation: { label: "Rotation", className: "bg-purple-100 text-purple-700 border-purple-200" }
    };
    return badges[priority] || null;
  };

  // Load a previous customer from today's history
  const loadPreviousCustomer = async (call) => {
    try {
      const response = await axios.get(`${API}/customers/${call.customer_id}`);
      setCustomer(response.data);
      setCallback(null);
      setPriority(null);
      setEditingPreviousCall(true);
      setShowTodaysHistory(false);
      
      // Pre-fill form with previous call data
      setJobWentWell(call.job_went_well);
      setSatisfactionScore(call.satisfaction_score);
      setHasIssues(call.has_issues);
      setIssueType(call.issue_type || "");
      setIssueNotes(call.issue_notes || "");
      setNpsScore(call.nps_score);
      setNotes(call.notes || "");
      
      toast.info("Fiche client chargée - Mode modification");
    } catch (error) {
      toast.error("Erreur lors du chargement du client");
    }
  };

  // Return to normal call flow
  const returnToCallFlow = () => {
    setEditingPreviousCall(false);
    resetForm();
    fetchNextCall(false);
  };

  // Generate all 24h time slots in 30-minute intervals
  const timeSlots = [];
  for (let hour = 0; hour < 24; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
  }

  // Quick select buttons for satisfaction and NPS
  const SatisfactionButton = ({ value }) => (
    <button
      type="button"
      onClick={() => setSatisfactionScore(value)}
      data-testid={`satisfaction-${value}`}
      className={`w-14 h-14 rounded-xl border-2 flex flex-col items-center justify-center transition-all ${
        satisfactionScore === value
          ? "border-indigo-500 bg-indigo-50 text-indigo-700 scale-105"
          : "border-slate-200 hover:border-slate-300 text-slate-600 hover:bg-slate-50"
      }`}
    >
      <Star className={`w-5 h-5 ${satisfactionScore >= value ? "fill-current text-amber-400" : ""}`} />
      <span className="text-xs mt-0.5 font-medium">{value}</span>
    </button>
  );

  const NPSButton = ({ value }) => (
    <button
      type="button"
      onClick={() => setNpsScore(value)}
      data-testid={`nps-${value}`}
      className={`w-11 h-11 rounded-lg border-2 flex items-center justify-center transition-all text-sm font-semibold ${
        npsScore === value
          ? value <= 6
            ? "border-rose-500 bg-rose-50 text-rose-700 scale-105"
            : value <= 8
            ? "border-amber-500 bg-amber-50 text-amber-700 scale-105"
            : "border-emerald-500 bg-emerald-50 text-emerald-700 scale-105"
          : "border-slate-200 hover:border-slate-300 text-slate-600 hover:bg-slate-50"
      }`}
    >
      {value}
    </button>
  );

  const QuickToggle = ({ label, value, onChange, yesLabel = "Oui", noLabel = "Non", yesKey = "O", noKey = "N" }) => (
    <div className="flex items-center gap-3">
      <span className="text-sm text-slate-600 min-w-[100px]">{label}</span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all flex items-center gap-2 ${
            value === true
              ? "border-emerald-500 bg-emerald-50 text-emerald-700"
              : "border-slate-200 hover:border-slate-300 text-slate-600"
          }`}
        >
          <ThumbsUp className="w-4 h-4" />
          {yesLabel}
          <kbd className="ml-1 px-1.5 py-0.5 text-xs bg-slate-100 rounded">{yesKey}</kbd>
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all flex items-center gap-2 ${
            value === false
              ? "border-rose-500 bg-rose-50 text-rose-700"
              : "border-slate-200 hover:border-slate-300 text-slate-600"
          }`}
        >
          <ThumbsDown className="w-4 h-4" />
          {noLabel}
          <kbd className="ml-1 px-1.5 py-0.5 text-xs bg-slate-100 rounded">{noKey}</kbd>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center">
                <Phone className="w-4 h-4 text-indigo-600" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-lg font-bold font-['Manrope'] text-slate-900">Vanessa</h1>
              </div>
            </div>
            {/* Company Selector - Always visible */}
            <div className="hidden sm:block">
              <CompanySelector />
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Mobile Company Selector */}
            <div className="sm:hidden">
              <CompanySelector />
            </div>
            {autoSaveStatus && (
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
                {autoSaveStatus === 'saving' ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Sauvegarde...</>
                ) : (
                  <><Save className="w-3 h-3 text-emerald-500" /> Sauvegardé</>
                )}
              </div>
            )}
            <button
              onClick={() => setShowShortcuts(true)}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
              title="Raccourcis clavier (?)"
            >
              <Keyboard className="w-4 h-4" />
            </button>
            <button
              onClick={() => { fetchMyCallStats(); setShowMyStats(true); }}
              className="relative flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-colors"
              title="Mes statistiques"
            >
              <BarChart3 className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-medium">Stats</span>
            </button>
            <button
              onClick={() => { fetchPendingCallbacks(); setShowCallbacksPanel(true); }}
              className="relative flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-600 transition-colors"
              title="Rappels en attente"
            >
              <Phone className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-medium">Rappels</span>
              {pendingCallbacks.length > 0 && (
                <span className="px-1.5 py-0.5 bg-amber-200 text-amber-800 text-xs rounded-full font-bold">
                  {pendingCallbacks.length}
                </span>
              )}
            </button>
            <button
              onClick={() => { fetchTodaysCalls(); setShowTodaysHistory(true); }}
              className="relative flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
              title="Historique du jour"
            >
              <History className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-medium">Aujourd'hui</span>
              {todaysCalls.length > 0 && (
                <span className="px-1.5 py-0.5 bg-slate-200 text-slate-700 text-xs rounded-full font-medium">
                  {todaysCalls.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setShowMessaging(true)}
              className="relative flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-colors"
              title="Messages"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-medium">Messages</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setShowNotifications(true)}
              className="relative flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 transition-colors"
              title="Notifications"
            >
              <Bell className="w-5 h-5" />
              {notifUnreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
                  {notifUnreadCount > 9 ? '9+' : notifUnreadCount}
                </span>
              )}
              {wsConnected && (
                <span className="absolute bottom-0 right-0 w-2 h-2 bg-emerald-500 rounded-full border border-white pointer-events-none" />
              )}
            </button>
            <span className="hidden sm:inline text-sm text-slate-600">{user?.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              data-testid="logout-btn"
              className="text-slate-500 hover:text-slate-700"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Messaging Panel */}
      <MessagingPanel 
        isOpen={showMessaging} 
        onClose={() => {
          setShowMessaging(false);
          refreshUnreadCount();
        }} 
      />

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications}
        unreadCount={notifUnreadCount}
        onMarkAsRead={markNotifAsRead}
        onMarkAllAsRead={markAllNotifsAsRead}
        connected={wsConnected}
      />

      {/* Sales Stats Banner */}
      {salesStats && salesStats.total_revenue > 0 && (
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-6 py-3">
          <div className="max-w-screen-2xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Euro className="w-5 h-5" />
                <span className="font-bold text-lg">
                  {salesStats.total_revenue.toLocaleString('fr-FR')}€ HT
                </span>
                <span className="text-emerald-100 text-sm">CA ce mois</span>
              </div>
              <div className="h-6 w-px bg-emerald-400" />
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                <span className="font-medium">{salesStats.total_sales} vente{salesStats.total_sales > 1 ? 's' : ''}</span>
              </div>
              {salesStats.average_sale > 0 && (
                <>
                  <div className="h-6 w-px bg-emerald-400" />
                  <div className="text-sm text-emerald-100">
                    Panier moyen: <span className="font-medium text-white">{Math.round(salesStats.average_sale).toLocaleString('fr-FR')}€ HT</span>
                  </div>
                </>
              )}
            </div>
            {Object.keys(salesStats.by_sales_rep || {}).length > 0 && (
              <div className="flex items-center gap-3 text-sm">
                <span className="text-emerald-100">Top vendeur:</span>
                {Object.entries(salesStats.by_sales_rep)
                  .sort((a, b) => b[1].revenue - a[1].revenue)
                  .slice(0, 1)
                  .map(([name, data]) => (
                    <span key={name} className="font-medium">
                      {name} ({data.revenue.toLocaleString('fr-FR')}€)
                    </span>
                  ))
                }
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      {!companyId ? (
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-60px)] p-8">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-bold font-['Manrope'] text-slate-900 mb-3">
              Aucune société sélectionnée
            </h2>
            <p className="text-slate-500 mb-8">
              Veuillez sélectionner une société dans le menu en haut à droite pour commencer à appeler.
            </p>
            <CompanySelector />
          </div>
        </div>
      ) : !customer ? (
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-60px)] p-8">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-6">
              <Phone className="w-10 h-10 text-indigo-600" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-bold font-['Manrope'] text-slate-900 mb-3">
              Prêt à appeler ?
            </h2>
            <p className="text-slate-500 mb-8">
              Appuyez sur <kbd className="px-2 py-1 bg-slate-100 rounded text-sm font-mono">Entrée</kbd> ou cliquez pour commencer
            </p>
            <Button
              onClick={() => fetchNextCall(false)}
              disabled={loading}
              data-testid="next-call-btn"
              className="h-14 px-10 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-lg shadow-lg shadow-indigo-200 transition-all"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Zap className="w-5 h-5 mr-2" />
              )}
              Prochain Appel
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-12 h-[calc(100vh-60px)] overflow-hidden">
          {/* Left Panel - Customer Info (Compact) */}
          <div className="col-span-2 border-r border-slate-200 bg-white p-4 overflow-y-auto">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Client</h2>
                {getPriorityBadge() && (
                  <Badge className={`${getPriorityBadge().className} border text-xs`}>
                    {getPriorityBadge().label}
                  </Badge>
                )}
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-slate-400">Nom</p>
                  <p className="font-semibold text-slate-900" data-testid="customer-name">{customer.name}</p>
                </div>
                
                <div>
                  <p className="text-xs text-slate-400">Téléphone</p>
                  <p className="font-medium text-slate-900 font-mono text-sm" data-testid="customer-phone">{customer.phone}</p>
                </div>
                
                <div>
                  <p className="text-xs text-slate-400">Ville</p>
                  <p className="text-sm text-slate-700">{customer.city}</p>
                </div>
                
                <div>
                  <p className="text-xs text-slate-400">Travaux</p>
                  <p className="text-sm text-slate-700">{customer.job_type}</p>
                </div>
                
                {customer.installation_date && (
                  <div>
                    <p className="text-xs text-slate-400">Installation</p>
                    <p className="text-sm text-slate-700">
                      {format(new Date(customer.installation_date), "d MMM yyyy", { locale: fr })}
                    </p>
                  </div>
                )}
              </div>

              {callback && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-1.5 text-amber-700 text-xs font-medium">
                    <Clock className="w-3 h-3" />
                    Rappel: {callback.scheduled_time}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Center Panel - Streamlined Call Form */}
          <div className="col-span-7 bg-slate-50 p-6 overflow-y-auto">
            <div className="max-w-3xl mx-auto space-y-6">
              {/* Editing Previous Call Banner */}
              {editingPreviousCall && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Edit3 className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">Mode modification</p>
                      <p className="text-sm text-amber-600">Vous consultez un appel précédent du jour</p>
                    </div>
                  </div>
                  <Button
                    onClick={returnToCallFlow}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Retour aux appels
                  </Button>
                </div>
              )}
              
              {/* Quick Answer Section */}
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-6 space-y-5">
                  <QuickToggle
                    label="Travaux OK ?"
                    value={jobWentWell}
                    onChange={setJobWentWell}
                    yesKey="O"
                    noKey="N"
                  />

                  <div className="border-t border-slate-100 pt-5">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="text-sm text-slate-600 min-w-[100px]">Satisfaction</span>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((v) => <SatisfactionButton key={v} value={v} />)}
                      </div>
                      <span className="text-xs text-slate-400 ml-2">Touches 1-5</span>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-5">
                    <QuickToggle
                      label="Problèmes ?"
                      value={hasIssues}
                      onChange={setHasIssues}
                      yesLabel="Oui"
                      noLabel="Non"
                      yesKey="P"
                      noKey="S"
                    />
                    
                    {hasIssues && (
                      <div className="mt-4 ml-[116px] space-y-3">
                        <Select value={issueType} onValueChange={setIssueType}>
                          <SelectTrigger data-testid="issue-type-select" className="w-64">
                            <SelectValue placeholder="Type de problème" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="qualite">Qualité</SelectItem>
                            <SelectItem value="delai">Délai</SelectItem>
                            <SelectItem value="communication">Communication</SelectItem>
                            <SelectItem value="prix">Prix</SelectItem>
                            <SelectItem value="proprete">Propreté</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                        <Textarea
                          value={issueNotes}
                          onChange={(e) => setIssueNotes(e.target.value)}
                          data-testid="issue-notes"
                          placeholder="Détails du problème..."
                          className="w-full"
                          rows={2}
                        />
                      </div>
                    )}
                  </div>

                  <div className="border-t border-slate-100 pt-5">
                    <div className="flex items-center gap-4 mb-2">
                      <span className="text-sm text-slate-600 min-w-[100px]">NPS (0-10)</span>
                      <div className="flex gap-1.5 flex-wrap">
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((v) => <NPSButton key={v} value={v} />)}
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 ml-[116px]">Alt + 0-9 (Alt+0 = 10)</p>
                    {npsScore !== null && (
                      <p className={`text-xs ml-[116px] mt-1 font-medium ${
                        npsScore <= 6 ? "text-rose-600" : npsScore <= 8 ? "text-amber-600" : "text-emerald-600"
                      }`}>
                        {npsScore <= 6 ? "Détracteur" : npsScore <= 8 ? "Passif" : "Promoteur"}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="call-notes"
                    placeholder="Notes de l'appel (optionnel)..."
                    rows={2}
                    className="resize-none"
                  />
                </CardContent>
              </Card>

              {/* Complete Button */}
              <Button
                id="complete-call-btn"
                onClick={handleCompleteCall}
                disabled={submitting}
                data-testid="complete-call-btn"
                className="w-full h-14 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-lg shadow-lg shadow-indigo-200 transition-all"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <ChevronRight className="w-5 h-5 mr-2" />
                )}
                Terminer & Suivant
                <kbd className="ml-3 px-2 py-1 bg-indigo-500 rounded text-sm">Entrée</kbd>
              </Button>
            </div>
          </div>

          {/* Right Panel - Quick Actions (Compact) */}
          <div className="col-span-3 border-l border-slate-200 bg-white p-4 overflow-y-auto">
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Actions</h2>

              {/* Send Review */}
              <Button
                onClick={handleSendReview}
                disabled={!canSendReview() || !customer?.email}
                data-testid="send-review-btn"
                className={`w-full justify-start ${
                  canSendReview() && customer?.email
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-slate-100 text-slate-400 cursor-not-allowed"
                }`}
              >
                <Mail className="w-4 h-4 mr-2" />
                Envoyer avis Google
              </Button>
              {!canSendReview() && (
                <p className="text-xs text-slate-400 px-1">
                  {getReviewBlockReason() || "Satisfaction ≥4 ou NPS ≥8, sans problème"}
                </p>
              )}
              {!customer?.email && canSendReview() && (
                <p className="text-xs text-amber-500 px-1">
                  Pas d'email client
                </p>
              )}
              {canSendReview() && customer?.email && (
                <p className="text-xs text-emerald-600 px-1">
                  Ouvre votre boîte mail
                </p>
              )}

              {/* Book Appointment */}
              <Button
                onClick={() => setShowAppointmentModal(true)}
                data-testid="book-appointment-btn"
                variant="outline"
                className="w-full justify-start border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                RDV commercial
                <kbd className="ml-auto px-1.5 py-0.5 text-xs bg-slate-100 rounded">R</kbd>
              </Button>

              {/* Schedule Callback */}
              <Button
                onClick={() => setShowCallbackModal(true)}
                data-testid="schedule-callback-btn"
                variant="outline"
                className="w-full justify-start border-amber-200 text-amber-700 hover:bg-amber-50"
              >
                <Clock className="w-4 h-4 mr-2" />
                Programmer rappel
                <kbd className="ml-auto px-1.5 py-0.5 text-xs bg-slate-100 rounded">C</kbd>
              </Button>

              {/* Unreachable - Quick action */}
              <Button
                onClick={handleUnreachable}
                disabled={submitting}
                data-testid="unreachable-btn"
                variant="outline"
                className="w-full justify-start border-orange-200 text-orange-600 hover:bg-orange-50"
              >
                <PhoneOff className="w-4 h-4 mr-2" />
                Injoignable
                <span className="ml-auto text-xs text-orange-400">+2h</span>
              </Button>

              {/* Do Not Contact */}
              <Button
                onClick={handleDoNotContact}
                data-testid="do-not-contact-btn"
                variant="outline"
                className="w-full justify-start border-rose-200 text-rose-600 hover:bg-rose-50"
              >
                <Ban className="w-4 h-4 mr-2" />
                Ne plus contacter
                <kbd className="ml-auto px-1.5 py-0.5 text-xs bg-slate-100 rounded">D</kbd>
              </Button>

              <div className="border-t border-slate-100 pt-3 mt-3">
                <Button
                  onClick={() => fetchNextCall(true)}
                  disabled={loading}
                  data-testid="skip-next-btn"
                  variant="ghost"
                  className="w-full justify-start text-slate-500 hover:text-slate-700"
                >
                  <ChevronRight className="w-4 h-4 mr-2" />
                  Passer
                  <kbd className="ml-auto px-1.5 py-0.5 text-xs bg-slate-100 rounded">Esc</kbd>
                </Button>
              </div>

              {/* Callback scheduled indicator */}
              {callbackDate && callbackTime && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                    <Clock className="w-4 h-4" />
                    Rappel: {format(callbackDate, "d MMM", { locale: fr })} à {callbackTime}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Appointment Modal */}
      <Dialog open={showAppointmentModal} onOpenChange={setShowAppointmentModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Prendre un rendez-vous</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Rule reminder */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                📅 Les RDV peuvent être programmés <span className="font-semibold">à partir de J+5</span> (5 jours minimum après aujourd'hui)
              </p>
            </div>
            
            <div>
              <Label className="text-slate-700">Commercial</Label>
              <Select value={selectedSalesRep} onValueChange={(v) => { setSelectedSalesRep(v); setAppointmentTime(""); }}>
                <SelectTrigger data-testid="appointment-sales-rep" className="mt-1">
                  <SelectValue placeholder="Sélectionnez" />
                </SelectTrigger>
                <SelectContent>
                  {salesReps.map((rep) => (
                    <SelectItem key={rep.id} value={rep.id}>{rep.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Date (à partir du {format(new Date(Date.now() + 5 * 24 * 60 * 60 * 1000), "d MMM", { locale: fr })})</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" data-testid="appointment-date-picker" className="w-full mt-1 justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {appointmentDate ? format(appointmentDate, "d MMM", { locale: fr }) : "Choisir"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={appointmentDate} onSelect={(d) => { setAppointmentDate(d); setAppointmentTime(""); }} disabled={(date) => {
                      // Teleprospector can only book appointments 5+ days from today
                      const minDate = new Date();
                      minDate.setDate(minDate.getDate() + 5);
                      minDate.setHours(0, 0, 0, 0);
                      return date < minDate;
                    }} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="text-slate-700">Durée</Label>
                <Select value={appointmentDuration.toString()} onValueChange={(v) => { setAppointmentDuration(parseInt(v)); setAppointmentTime(""); }}>
                  <SelectTrigger data-testid="appointment-duration" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="60">1 heure</SelectItem>
                    <SelectItem value="90">1h30</SelectItem>
                    <SelectItem value="120">2 heures</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-700 flex items-center gap-2">
                Créneaux disponibles
                {loadingSlots && <Loader2 className="w-3 h-3 animate-spin" />}
              </Label>
              {!selectedSalesRep || !appointmentDate ? (
                <p className="text-sm text-slate-400 mt-2">Sélectionnez un commercial et une date</p>
              ) : availableSlots.length === 0 && !loadingSlots ? (
                <p className="text-sm text-amber-600 mt-2">Aucun créneau disponible ce jour</p>
              ) : (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setAppointmentTime(slot)}
                      data-testid={`slot-${slot}`}
                      className={`py-2 px-3 text-sm rounded-lg border-2 transition-all ${
                        appointmentTime === slot
                          ? "border-indigo-500 bg-indigo-50 text-indigo-700 font-medium"
                          : "border-slate-200 hover:border-slate-300 text-slate-600"
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Personal appointments info */}
              {personalAppointments.length > 0 && (
                <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs font-medium text-amber-700 mb-2">📍 RDV personnels du commercial :</p>
                  <div className="space-y-1">
                    {personalAppointments.map((apt, idx) => (
                      <p key={idx} className="text-xs text-amber-600">
                        • {apt.time} - <span className="font-medium">{apt.city}</span> {apt.purpose && apt.purpose !== 'RDV personnel' && `(${apt.purpose})`}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="text-slate-700">Objet</Label>
              <Input
                value={appointmentPurpose}
                onChange={(e) => setAppointmentPurpose(e.target.value)}
                data-testid="appointment-purpose"
                placeholder="Ex: Devis rénovation"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAppointmentModal(false)}>Annuler</Button>
            <Button onClick={handleBookAppointment} data-testid="confirm-appointment-btn" className="bg-indigo-600 hover:bg-indigo-700">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Callback Modal */}
      <Dialog open={showCallbackModal} onOpenChange={setShowCallbackModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Programmer un rappel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="callback-date-picker" className="w-full mt-1 justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {callbackDate ? format(callbackDate, "d MMMM yyyy", { locale: fr }) : "Choisir"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={callbackDate} onSelect={setCallbackDate} disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="text-slate-700">Heure</Label>
              <Select value={callbackTime} onValueChange={setCallbackTime}>
                <SelectTrigger data-testid="callback-time" className="mt-1">
                  <SelectValue placeholder="Heure" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCallbackModal(false)}>Annuler</Button>
            <Button onClick={handleScheduleCallback} data-testid="confirm-callback-btn" className="bg-amber-600 hover:bg-amber-700">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Keyboard Shortcuts Modal */}
      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <Keyboard className="w-5 h-5" />
              Raccourcis clavier
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900">Réponses rapides</h4>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-slate-600">Travaux OK</span><kbd className="px-2 py-1 bg-slate-100 rounded">O</kbd></div>
                  <div className="flex justify-between"><span className="text-slate-600">Travaux pas OK</span><kbd className="px-2 py-1 bg-slate-100 rounded">N</kbd></div>
                  <div className="flex justify-between"><span className="text-slate-600">Problème</span><kbd className="px-2 py-1 bg-slate-100 rounded">P</kbd></div>
                  <div className="flex justify-between"><span className="text-slate-600">Sans problème</span><kbd className="px-2 py-1 bg-slate-100 rounded">S</kbd></div>
                  <div className="flex justify-between"><span className="text-slate-600">Satisfaction 1-5</span><kbd className="px-2 py-1 bg-slate-100 rounded">1-5</kbd></div>
                  <div className="flex justify-between"><span className="text-slate-600">NPS 0-10</span><kbd className="px-2 py-1 bg-slate-100 rounded">Alt + 0-9</kbd></div>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900">Actions</h4>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-slate-600">Terminer & Suivant</span><kbd className="px-2 py-1 bg-slate-100 rounded">Entrée</kbd></div>
                  <div className="flex justify-between"><span className="text-slate-600">Passer au suivant</span><kbd className="px-2 py-1 bg-slate-100 rounded">Esc</kbd></div>
                  <div className="flex justify-between"><span className="text-slate-600">RDV commercial</span><kbd className="px-2 py-1 bg-slate-100 rounded">R</kbd></div>
                  <div className="flex justify-between"><span className="text-slate-600">Programmer rappel</span><kbd className="px-2 py-1 bg-slate-100 rounded">C</kbd></div>
                  <div className="flex justify-between"><span className="text-slate-600">Ne plus contacter</span><kbd className="px-2 py-1 bg-slate-100 rounded">D</kbd></div>
                  <div className="flex justify-between"><span className="text-slate-600">Aide raccourcis</span><kbd className="px-2 py-1 bg-slate-100 rounded">?</kbd></div>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-sm text-emerald-700">
                <Save className="w-4 h-4 inline mr-1" />
                <strong>Sauvegarde automatique</strong> - Vos réponses sont sauvegardées automatiquement
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowShortcuts(false)} className="bg-indigo-600 hover:bg-indigo-700">Compris !</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* My Stats Modal */}
      <Dialog open={showMyStats} onOpenChange={setShowMyStats}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              Mes statistiques
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {myCallStats ? (
              <div className="space-y-6">
                {/* Today/Week/Month */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-indigo-50 rounded-xl">
                    <p className="text-3xl font-bold text-indigo-600">{myCallStats.calls_today}</p>
                    <p className="text-xs text-indigo-500 mt-1">Aujourd'hui</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-3xl font-bold text-slate-700">{myCallStats.calls_week}</p>
                    <p className="text-xs text-slate-500 mt-1">Cette semaine</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-3xl font-bold text-slate-700">{myCallStats.calls_month}</p>
                    <p className="text-xs text-slate-500 mt-1">Ce mois</p>
                  </div>
                </div>

                {/* Performance metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-amber-500 fill-current" />
                      <span className="text-sm text-emerald-700">Satisfaction moy.</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-600">{myCallStats.avg_satisfaction}/5</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-blue-700">Score NPS</span>
                    </div>
                    <p className={`text-2xl font-bold ${myCallStats.nps_score >= 50 ? 'text-emerald-600' : myCallStats.nps_score >= 0 ? 'text-amber-600' : 'text-rose-600'}`}>
                      {myCallStats.nps_score}
                    </p>
                  </div>
                </div>

                {/* Other metrics */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">RDV pris ce mois</span>
                    <span className="font-semibold text-slate-900">{myCallStats.rdv_month}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Rappels en attente</span>
                    <span className="font-semibold text-amber-600">{myCallStats.callbacks_pending}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Problèmes signalés</span>
                    <span className="font-semibold text-rose-600">{myCallStats.issues_month}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
                <p className="text-slate-500 mt-2">Chargement...</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowMyStats(false)} className="bg-emerald-600 hover:bg-emerald-700">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Callbacks Panel */}
      {showCallbacksPanel && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowCallbacksPanel(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-amber-600" />
                <h2 className="font-semibold text-slate-900">Rappels en attente</h2>
                <Badge className="bg-amber-100 text-amber-700 border-amber-200">{pendingCallbacks.length}</Badge>
              </div>
              <button
                onClick={() => setShowCallbacksPanel(false)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto h-[calc(100%-65px)] p-4">
              {pendingCallbacks.length === 0 ? (
                <div className="text-center py-12">
                  <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucun rappel en attente</p>
                  <p className="text-slate-400 text-sm mt-1">Les rappels programmés apparaîtront ici</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingCallbacks.map((cb) => {
                    const isToday = cb.scheduled_date === format(new Date(), "yyyy-MM-dd");
                    const isPast = isToday && cb.scheduled_time < format(new Date(), "HH:mm");
                    return (
                      <div
                        key={cb.id}
                        className={`p-4 rounded-xl border transition-all cursor-pointer group ${
                          isPast 
                            ? "bg-rose-50 border-rose-200 hover:border-rose-300" 
                            : isToday 
                            ? "bg-amber-50 border-amber-200 hover:border-amber-300"
                            : "bg-slate-50 border-slate-200 hover:border-indigo-300"
                        }`}
                        onClick={() => loadCustomerFromCallback(cb)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{cb.customer_name}</p>
                              {isPast && (
                                <Badge className="bg-rose-100 text-rose-700 text-xs">En retard</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">{cb.customer_city}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className={`flex items-center gap-1 text-sm font-medium ${
                                isPast ? "text-rose-600" : isToday ? "text-amber-600" : "text-slate-600"
                              }`}>
                                <Clock className="w-3.5 h-3.5" />
                                {cb.scheduled_time}
                              </span>
                              <span className="text-sm text-slate-500">
                                {isToday ? "Aujourd'hui" : format(new Date(cb.scheduled_date), "d MMM", { locale: fr })}
                              </span>
                            </div>
                            {cb.customer_phone && (
                              <p className="text-xs text-slate-400 mt-1">{cb.customer_phone}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); cancelCallback(cb.id); }}
                              className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                              title="Annuler le rappel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity">
                              <ChevronRight className="w-4 h-4" />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Today's History Slide Panel */}
      {showTodaysHistory && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowTodaysHistory(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-slate-600" />
                <h2 className="font-semibold text-slate-900">Appels du jour</h2>
                <Badge className="bg-slate-100 text-slate-700 border-slate-200">{todaysCalls.length}</Badge>
              </div>
              <button
                onClick={() => setShowTodaysHistory(false)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto h-[calc(100%-65px)] p-4">
              {todaysCalls.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucun appel aujourd'hui</p>
                  <p className="text-slate-400 text-sm mt-1">Les appels effectués apparaîtront ici</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todaysCalls.map((call) => (
                    <div
                      key={call.id}
                      className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all cursor-pointer group"
                      onClick={() => loadPreviousCustomer(call)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{call.customer_name || "Client"}</p>
                          <p className="text-sm text-slate-500">{call.customer_city}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {format(new Date(call.created_at), "HH:mm", { locale: fr })}
                            </span>
                            {call.satisfaction_score && (
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 text-amber-400 fill-current" />
                                {call.satisfaction_score}/5
                              </span>
                            )}
                            {call.nps_score !== null && (
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                call.nps_score >= 9 ? "bg-emerald-100 text-emerald-700" :
                                call.nps_score >= 7 ? "bg-amber-100 text-amber-700" :
                                "bg-rose-100 text-rose-700"
                              }`}>
                                NPS {call.nps_score}
                              </span>
                            )}
                          </div>
                          {call.notes && (
                            <p className="text-xs text-slate-400 mt-2 line-clamp-2">{call.notes}</p>
                          )}
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">
                            <Edit3 className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeleprospectorDashboard;
